import {useCallback, useEffect, useRef, useState} from 'react';
import type {PointerEvent} from 'react';

// Hover intent: the pointer must rest on a trigger this long before its row opens, so rows
// shifting while a panel collapses do not snap open whichever row slides under a still pointer.
const DEFAULT_HOVER_DELAY_MS = 120;

/**
 * Single-open accordion that opens on mouse hover and toggles on click/tap/keyboard.
 *
 * - Mouse: resting on a trigger opens it (after `delay`); a click on the open row keeps it open,
 *   since the hover already opened it.
 * - Touch, pen and keyboard (Enter/Space): a click toggles the row open and closed as usual.
 *
 * Spread `getTriggerProps(index)` onto each row's trigger button.
 */
export function useHoverAccordion(initial: number | null = 0, delay = DEFAULT_HOVER_DELAY_MS) {
    const [open, setOpen] = useState<number | null>(initial);
    const hoverTimer = useRef<ReturnType<typeof setTimeout>>();
    // Pointer type of the press that produced the next click; empty for keyboard activation.
    const pressPointer = useRef('');

    useEffect(() => () => clearTimeout(hoverTimer.current), []);

    const getTriggerProps = useCallback(
        (index: number) => ({
            onPointerEnter: (event: PointerEvent) => {
                if (event.pointerType !== 'mouse') return;
                clearTimeout(hoverTimer.current);
                hoverTimer.current = setTimeout(() => setOpen(index), delay);
            },
            onPointerLeave: () => clearTimeout(hoverTimer.current),
            onPointerDown: (event: PointerEvent) => {
                pressPointer.current = event.pointerType;
            },
            onClick: () => {
                clearTimeout(hoverTimer.current);
                const byMouse = pressPointer.current === 'mouse';
                pressPointer.current = '';
                setOpen((current) => {
                    if (current !== index) return index;
                    return byMouse ? current : null;
                });
            }
        }),
        [delay]
    );

    return {open, setOpen, getTriggerProps};
}
